import React from 'react';

// Helper to get cache object from localStorage
function getCache(key) {
    const raw = localStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : null;
    const data = parsed && typeof parsed === 'object' && 'data' in parsed ? parsed.data : parsed;
    if (!data) return { data: null, lastFetched: null };
    try {
        if (data && typeof data === 'object' && 'data' in data && 'lastFetched' in data) {
            return data;
        } else {
            // Backward compatibility: treat as data only
            return { data: data, lastFetched: null };
        }
    } catch {
        return { data: null, lastFetched: null };
    }
}

// Profile Overlay Component
const ProfileOverlay = ({ player, onClose }) => {
    if (!player) return null;

    // Helper function to format location
    const formatLocation = (location) => {
        if (!location) return 'Location not set';
        if (typeof location === 'string') return location;

        // Handle different location structure formats
        if (location.SUBURB && location.CITY && location.PROVINCE) {
            return `${location.SUBURB}, ${location.CITY}, ${location.PROVINCE}`;
        }

        // Handle Google Maps API format
        const parts = [];
        if (location.sublocality) parts.push(location.sublocality);
        if (location.locality) parts.push(location.locality);
        if (location.administrative_area_level_1) parts.push(location.administrative_area_level_1);
        if (location.country) parts.push(location.country);

        return parts.length > 0 ? parts.join(', ') : 'Location not set';
    };

    // Get the most up-to-date points from rankings if available
    const getRankingsData = () => {
        try {
            // If this is the logged-in user, always use userProfile
            const userProfile = getCache('userProfile').data;
            if (userProfile && (userProfile.userId === player.userId || userProfile.name === player.name)) {
                return userProfile;
            }
            
            // For other players, load combined players from singlesRankings and doublesRankings
            const singlesRankings = getCache('singlesRankings').data;
            const doublesRankings = getCache('doublesRankings').data;
            
            let allPlayers = [];
            if (singlesRankings && doublesRankings) {
                // Combine and deduplicate players from both rankings
                const singlesPlayers = singlesRankings.rankings || [];
                const doublesPlayers = doublesRankings.rankings || [];
                allPlayers = [...singlesPlayers, ...doublesPlayers];

                // Deduplicate by userId (fallback to name if userId is missing)
                const uniquePlayersMap = {};
                allPlayers.forEach(player => {
                    const key = player.userId || player.name;
                    if (key && !uniquePlayersMap[key]) {
                        uniquePlayersMap[key] = player;
                    }
                });
                allPlayers = Object.values(uniquePlayersMap);
            } else if (singlesRankings) {
                allPlayers = singlesRankings.rankings || [];
            } else if (doublesRankings) {
                allPlayers = doublesRankings.rankings || [];
            }
            
            const playerRankings = allPlayers.find(p => p.name === player.name || p.userId === player.userId);
            return playerRankings || player;
        } catch (error) {
            console.error('Error parsing rankings:', error);
            return player;
        }
    };

    const playerData = getRankingsData();

    console.log("Player data in overlay:", playerData); // Debug log

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 relative">
                {/* Close button */}
                <button 
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                >
                    ✕
                </button>

                {/* Profile Content */}
                <div className="flex flex-col items-center">
                    {playerData.picture ? (
                        <div className="w-32 h-32 rounded-full overflow-hidden mb-4">
                            <img 
                                src={playerData.picture} 
                                alt={playerData.name} 
                                className="h-full w-full object-cover"
                            />
                        </div>
                    ) : (
                        <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center mb-4">
                            <span className="text-4xl text-gray-500">{playerData.name[0]}</span>
                        </div>
                    )}
                    <h3 className="text-xl font-semibold mb-2">{playerData.name}</h3>
                    <div className="text-gray-600 text-center">
                        <p className="mb-2">Age: {playerData.age || 'N/A'}</p>
                        <p className="mb-2">Gender: {playerData.gender || 'N/A'}</p>
                        <p className="mb-2">Location: {formatLocation(playerData.location)}</p>
                        <div className="mt-4 grid grid-cols-2 gap-4 text-center">
                            <div>
                                <p className="font-semibold">Singles Points</p>
                                <p>{playerData.singlesPoints || 0}</p>
                            </div>
                            <div>
                                <p className="font-semibold">Doubles Points</p>
                                <p>{playerData.doublesPoints || 0}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProfileOverlay; 